import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { Loader2, ArrowLeft } from "lucide-react";

const Shell = ({ brand, wsId, children }) => (
  <div className="min-h-screen bg-background text-foreground">
    <header className="border-b border-border">
      <div className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
        <Link to={`/b/${wsId}`} className="font-display font-bold text-lg" data-testid="public-blog-brand">{brand}</Link>
        <span className="text-xs text-muted-foreground">Blog</span>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-5 py-10">{children}</main>
  </div>
);

export default function PublicBlog() {
  const { wsId, slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setData(null); setMissing(false);
    api.get(slug ? `/public-blog/${wsId}/${slug}` : `/public-blog/${wsId}`).then((r) => setData(r.data)).catch(() => setMissing(true));
  }, [wsId, slug]);
  if (missing) return <Shell brand="Blog" wsId={wsId}><p data-testid="public-blog-missing">{slug ? "Article not found." : "This blog doesn't exist."}</p></Shell>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  if (slug) {
    return (
      <Shell brand={data.brand} wsId={wsId}>
        <Helmet><title>{data.meta_title || data.article_title}</title><meta name="description" content={data.meta_description || ""} /></Helmet>
        <Link to={`/b/${wsId}`} className="text-xs text-muted-foreground inline-flex items-center gap-1 mb-6 hover:text-foreground"><ArrowLeft className="h-3 w-3" /> All articles</Link>
        <article className="ags-article" data-testid="public-blog-article" dangerouslySetInnerHTML={{ __html: data.rendered_html }} />
      </Shell>
    );
  }
  return (
    <Shell brand={data.brand} wsId={wsId}>
      <Helmet><title>{`${data.brand} Blog`}</title></Helmet>
      <h1 className="text-4xl font-display font-bold mb-8">{data.brand} Blog</h1>
      {data.articles.length === 0 ? <p className="text-muted-foreground">No articles yet.</p> : (
        <div className="space-y-6" data-testid="public-blog-list">
          {data.articles.map((a) => (
            <Link key={a.article_slug} to={`/b/${wsId}/${a.article_slug}`} className="flex gap-4 group" data-testid={`public-blog-item-${a.article_slug}`}>
              {a.cover && <img src={a.cover} alt="" className="h-24 w-32 object-cover rounded-md border border-border shrink-0" loading="lazy" />}
              <span>
                <span className="block font-semibold text-lg group-hover:text-primary transition-colors">{a.article_title}</span>
                <span className="block text-sm text-muted-foreground mt-1">{a.meta_description}</span>
                <span className="block text-xs text-muted-foreground mt-1">{a.published_at ? new Date(a.published_at).toLocaleDateString() : ""}</span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </Shell>
  );
}
